import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  Avatar,
  Paper,
  IconButton,
  Skeleton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from '@mui/material';
import {
  AddCircleRounded,
  AssignmentOutlined,
  CheckCircleRounded,
  LocationOnOutlined,
  LockOutlined,
} from '@mui/icons-material';

import { useSelector } from 'react-redux';
import useGetLocations from '../../Hooks/useGetLocation';
import { useGetStudents } from '../../Hooks/useGetStudents';
import useGetAssessments from '../../Hooks/useGetAssessments';

// ─── Avatar palette ───────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  { bg: '#bbdefb', color: '#1565c0' },
  { bg: '#c8e6c9', color: '#1b5e20' },
  { bg: '#ffe0b2', color: '#bf360c' },
  { bg: '#e1bee7', color: '#4a148c' },
  { bg: '#b2ebf2', color: '#006064' },
];

function StudentAvatar({ firstName, lastName, index = 0 }) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  const { bg, color } = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return (
    <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, bgcolor: bg, color }}>
      {initials}
    </Avatar>
  );
}

// ─── Sticky table header cell ─────────────────────────────────────────────────
function TH({ children, width }) {
  return (
    <TableCell
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '2px solid',
        borderColor: 'divider',
        py: 0.875,
        width,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase' }}
      >
        {children}
      </Typography>
    </TableCell>
  );
}

// ─── Step badge ───────────────────────────────────────────────────────────────
function StepBadge({ number, done }) {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: done ? 'success.main' : 'primary.main',
        flexShrink: 0,
      }}
    >
      {done ? (
        <CheckCircleRounded sx={{ fontSize: 14, color: 'primary.contrastText' }} />
      ) : (
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'primary.contrastText', lineHeight: 1 }}>
          {number}
        </Typography>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function StudentsTable({ onSelectAction }) {
  const role = useSelector((state) => state.role);

  const { students, loading: loadingStudents, fetchStudents } = useGetStudents();
  const { locations, loading: loadingLocations }              = useGetLocations(role);
  const { assessments, fetchAssessments }                     = useGetAssessments();

  const [locationId,    setLocationId]    = useState(null);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(new Set());

  // Load assessments on mount; students load only after a location is chosen
  useEffect(() => {
    fetchAssessments();
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleLocationChange = async (e) => {
    const val = e.target.value;
    setLocationId(val);
    await fetchStudents(val);
  };

  const handleAssessmentChange = (e, student) => {
    setSelectedAssessmentId((prev) =>({...prev, [student.id]: {"assessment_id": e.target.value, "easy_score": true}}));
  };

  const handleSelect = (student) => {
    if (selectedIds.has(student.id)) return; // already added
    const content = selectedAssessmentId[student.id]; // Grab the assessment
    setSelectedIds((prev) => new Set([...prev, student.id])); // Place in general completed id
    onSelectAction?.({ ...student, ...content});
  };


  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── STEP 1 + STEP 2: filter bar ── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: 'hidden' }}
      >
  

        {/* Step 2 — Location (optional filter) */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          sx={{ px: 2, py: 1.25 }}
        >

          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1, mb: 0.5 }}>
              Filter by Location
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationOnOutlined sx={{ fontSize: 14 }} />
                  <span>Location</span>
                </Stack>
              </InputLabel>
              <Select
                value={locationId ?? ''}
                label="Location"
                onChange={handleLocationChange}
              >
                <MenuItem value="">All locations</MenuItem>

                {/* TUTOR: see their own locations */}
                {role?.role === 'TUTOR' &&
                  role.tutor_locations?.map((item, i) => (
                    <MenuItem value={item.id} key={i}>
                      <Typography variant="body2">{item.location_name}</Typography>
                    </MenuItem>
                  ))}

                {/* ADMIN / ROOT: see all locations */}
                {(role?.role === 'ADMIN' || role?.role === 'ROOT') &&
                  locations?.map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      <Stack>
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.city}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>


      {/* ── TABLE ── */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 1.5,
          overflow: 'hidden',
          opacity: 1,
          pointerEvents: 'auto',
          transition: 'opacity 0.2s ease',
        }}
      >
        <TableContainer sx={{ maxHeight: '30vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TH>Student</TH>
                <TH width={80}>Grade</TH>
                <TH width={80}>Status</TH>
                <TH width={200}>Assessment</TH>
                <TH width={48} />
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Skeleton rows while loading */}
              {loadingStudents &&
                [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ borderColor: 'divider', py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton width={120} height={16} />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderColor: 'divider' }}><Skeleton width={40} height={16} /></TableCell>
                    <TableCell sx={{ borderColor: 'divider' }}><Skeleton width={50} height={20} /></TableCell>
                    <TableCell sx={{ borderColor: 'divider' }} />
                  </TableRow>
                ))}

              {/* Empty state */}
              {!loadingStudents && students?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 3, textAlign: 'center', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.disabled">
                      No students found
                    </Typography>
                    {!locationId && (
                      <Typography variant="caption" color="text.disabled">
                        Try selecting a location above
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* Student rows */}
              {!loadingStudents &&
                students.filter((value) => !selectedIds.has(value))?.map((student, idx) => {
                  const isSelected = selectedIds.has(student.id);
                  const isActiveAssessment = selectedAssessmentId[student.id];
                  return (
                    <TableRow
                      key={student.id}
                      hover={!isSelected}
                      sx={{
                        bgcolor: isSelected ? 'action.selected' : 'background.paper',
                        opacity: isSelected ? 0.6 : 1,
                        cursor: isSelected ? 'default' : 'pointer',
                        transition: 'background 0.12s ease, opacity 0.12s ease',
                        '&:hover': !isSelected ? { bgcolor: 'action.hover' } : {},
                      }}
                    >
                      {/* Name + email */}
                      <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <StudentAvatar
                            firstName={student.first_name}
                            lastName={student.last_name}
                            index={idx}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={isSelected ? 400 : 500} noWrap>
                              {student.first_name} {student.last_name}
                            </Typography>
                            {student.email && (
                              <Typography variant="caption" color="text.disabled" noWrap>
                                {student.email}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Grade */}
                      <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                          {student.grade_level ? `G${student.grade_level}` : '—'}
                        </Typography>
                      </TableCell>

                      {/* Active status */}
                      <TableCell sx={{ py: 0.75, borderColor: 'divider' }}>
                        <Chip
                          label={student.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={student.active ? 'success' : 'default'}
                          variant={student.active ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.68rem', height: 20, borderRadius: 1, fontWeight: 600 }}
                        />
                      </TableCell>
                      { /** Assessment select */}
                      <TableCell>
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1, mb: 0.5 }}>
                          Select Assessment <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                        </Typography>
                        <FormControl size="small" fullWidth>
                          <InputLabel>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AssignmentOutlined sx={{ fontSize: 14 }} />
                              <span>Assessment</span>
                            </Stack>
                          </InputLabel>
                          <Select
                            value={isActiveAssessment ? isActiveAssessment.assessment_id :''}
                            label="Assessment"
                            onChange={(e) => {e.stopPropagation();handleAssessmentChange(e, student)}}
                            size='small'
                          >
                            {assessments.filter((val) => val.easy_score === true)?.map((item) => (
                              <MenuItem value={item.id} key={item.id}>
                                <Typography variant="body2">{item.title}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      </TableCell>

                      {/* Add / added action */}
                      <TableCell sx={{ py: 0.75, borderColor: 'divider', pr: 1 }}>
                        {isSelected ? (
                          <Tooltip title="Assessment assigned">
                            <CheckCircleRounded sx={{ fontSize: 18, color: 'success.main', display: 'block' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={isActiveAssessment ? 'Add to list' : 'Select an assessment first'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isActiveAssessment}
                                onClick={(e) => { e.stopPropagation(); handleSelect(student); }}
                                sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                              >
                                <AddCircleRounded fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}